import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { loadArtworkImageJpeg, loadBytes } from "@services/pdf/assetLoader"
import { assembleDocument, IAssembledPdf, IPdfStrings } from "@services/pdf/generator"
import { openPdf } from "@services/pdf/rasterize"
import { TPdfLayoutType } from "@services/pdf/types"
import * as styles from "./PdfBuilder.module.scss"
import { IBuilderAssets } from "./hooks/useBuilderAssets"
import { IBuilderState } from "./state/reducer"

// full-quality export cap on the longest image side (memory guard)
const exportImageMaxSide = 2400

export interface IPreviewState {
	status: "generating" | "ready" | "error"
	progress: number
	total: number
	result: IAssembledPdf | null
}

/**
 * Generates the assembled document when the preview step opens (iOS previewPDF).
 * Takes its inputs explicitly — it runs in the component that RENDERS the
 * BuilderProvider, so the context is not available to it.
 */
export function usePreviewDocument(
	enabled: boolean,
	state: IBuilderState,
	assets: IBuilderAssets,
	strings: IPdfStrings,
	layoutType: TPdfLayoutType
): IPreviewState {
	const [preview, setPreview] = useState<IPreviewState>({ status: "generating", progress: 0, total: 0, result: null })

	useEffect(() => {
		if (!enabled) {
			setPreview({ status: "generating", progress: 0, total: 0, result: null })
			return
		}
		let cancelled = false

		assembleDocument({
			coverRow: state.coverRow,
			contentRows: state.contentRows,
			options: state.options,
			strings,
			layoutType,
			backCoverBytes: assets.backCoverBytes,
			loadRowImage: (row) => {
				const imageUrl = row.pages[0]?.imageUrl ?? null
				return imageUrl ? loadArtworkImageJpeg(imageUrl, exportImageMaxSide) : Promise.resolve(null)
			},
			loadAttachment: (pdfPath) => loadBytes(pdfPath),
			onProgress: (progress, total) => {
				if (!cancelled) {
					setPreview((prev) => ({ ...prev, progress, total }))
				}
			},
		})
			.then((result) => {
				if (!cancelled) {
					setPreview((prev) => ({ ...prev, status: "ready", result }))
				}
			})
			.catch(() => {
				if (!cancelled) {
					setPreview((prev) => ({ ...prev, status: "error" }))
				}
			})

		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled])

	return preview
}

const ProgressRing: React.FC<{ progress: number; total: number }> = ({ progress, total }) => {
	const fraction = total > 0 ? progress / total : 0
	const radius = 26
	const circumference = 2 * Math.PI * radius
	return (
		<div className={styles.progressRing}>
			<svg width="64" height="64">
				<circle cx="32" cy="32" r={radius} stroke="#e3e3e3" strokeWidth="5" fill="none" />
				<circle
					cx="32"
					cy="32"
					r={radius}
					stroke="#282828"
					strokeWidth="5"
					fill="none"
					strokeDasharray={circumference}
					strokeDashoffset={circumference * (1 - fraction)}
					strokeLinecap="round"
				/>
			</svg>
			<span className={styles.progressLabel}>{Math.round(fraction * 100)}%</span>
		</div>
	)
}

const PdfViewer: React.FC<{ bytes: Uint8Array }> = ({ bytes }) => {
	const { t } = useTranslation()
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		const container = containerRef.current
		if (!container) {
			return
		}
		let cancelled = false
		let destroy: (() => void) | null = null
		setFailed(false)

		const render = async () => {
			const handle = await openPdf(bytes)
			destroy = () => handle.destroy()
			if (cancelled) {
				handle.destroy()
				return
			}
			const targetWidth = Math.min(container.clientWidth - 32, 700)
			for (let i = 0; i < handle.pageCount; i++) {
				if (cancelled) {
					return
				}
				const { canvas } = await handle.renderPage(i, targetWidth)
				if (cancelled) {
					return
				}
				const wrapper = document.createElement("div")
				wrapper.className = styles.previewPage
				wrapper.appendChild(canvas)
				container.appendChild(wrapper)
			}
		}
		// surface pdfjs failures (e.g. worker missing from the deploy) instead
		// of leaving a silently blank stage
		render().catch(() => {
			if (!cancelled) {
				setFailed(true)
			}
		})

		return () => {
			cancelled = true
			destroy?.()
			if (container) {
				container.innerHTML = ""
			}
		}
	}, [bytes])

	if (failed) {
		return <div className={styles.centerLoading}>{t("pdfBuilder.previewFailed")}</div>
	}
	return <div ref={containerRef} className={styles.previewStage} data-testid="pdf-preview-pages" />
}

export const PreviewStep: React.FC<{ preview: IPreviewState }> = ({ preview }) => {
	const { t } = useTranslation()
	if (preview.status === "error") {
		return <div className={styles.centerLoading}>{t("pdfBuilder.previewFailed")}</div>
	}
	if (preview.status === "generating" || !preview.result) {
		return (
			<div className={styles.centerLoading}>
				<ProgressRing progress={preview.progress} total={preview.total} />
				<span>{t("pdfBuilder.generating")}</span>
			</div>
		)
	}
	return (
		<>
			{preview.result.missingImageCount > 0 && (
				<div className={styles.imagesWarning} role="alert" data-testid="pdf-images-warning">
					{t("pdfBuilder.imagesUnavailable", { count: preview.result.missingImageCount })}
				</div>
			)}
			<PdfViewer bytes={preview.result.bytes} />
		</>
	)
}
